import { FC, FunctionComponent, memo, useCallback, useContext, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { IEventFormMetadata, IFormFieldMetadata, PostUpdateMonitoringVisitData, IPatient, ISourceData, ITrial, ITrialFormMetadata, PostSaveSourceDataConfiguration, ISourceDataConfiguration, ISourceDataConfigurationEvent, ISourceDataEvent, IEventMetadata, ISourceDataConfigurationForm, IFormMetadata, ISourceDataConfigurationFormVersion, IFormVersionMetadata, ISourceDataForm, ISourceDataConfigurationFormSection, IFormSectionMetadata, ISourceDataConfigurationFormField, ISourceDataField, IInstitution, ISourceDataFinding, PostUpdateSourceDataStatus } from '@/api/dtos';
import { Divider, List, LinearProgress, Box, Paper, Grid, Tabs, Tab, Fade, styled, alpha, Typography, IconButton, Chip, Tooltip } from '@mui/material'
import { Fragment } from 'react';
import { BasicAlert, Form, FormsFeatureContext, FormSubmitButton, InputOnlyField, IValidationError, Text, IHasChildren } from '@ngt/forms';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faCogs, faInfoCircle, faRefresh, faSquareList } from '@fortawesome/duotone-light-svg-icons';
import { IFormSubmit, Observable } from '@ngt/forms-core';
import { useNavigate, useParams } from 'react-router-dom';
import SkeletonExt from '@/components/layouts/SkeletonExt';
import SourceDataFormSettings from './SourceDataFormSettings';
import { useLocalStorage } from 'react-use';
import SourceDataFormProcessor, { ISourceDataFormLayoutComponents, ISourceDataFormSettings, ISourceDataVerificationEvent, ISourceDataVerificationField, ISourceDataVerificationForm, ISourceDataVerificationItem, ISourceDataVerificationSection, SourceDataType } from '@/utilities/forms/SourceDataFormProcessor';
import SourceDataFormEventExpanding from './expanding/SourceDataFormEventExpanding';
import SourceDataFormFormExpanding from './expanding/SourceDataFormFormExpanding';
import SourceDataFormCollectionExpanding from './expanding/SourceDataFormCollectionExpanding';
import SourceDataFormGroupExpanding from './expanding/SourceDataFormGroupExpanding';
import SourceDataFormGroupTitled from './titled/SourceDataFormGroupTitled';
import SourceDataFormCollectionTitled from './titled/SourceDataFormCollectionTitled';
import SourceDataFormFormTitled from './titled/SourceDataFormFormTitled';
import SourceDataFormEventTitled from './titled/SourceDataFormEventTitled';
import SourceDataFormFieldTitled from './titled/SourceDataFormFieldTitled';
import SourceDataFormFieldExpanding from './expanding/SourceDataFormFieldExpanding';
import SourceDataFormLayoutExpanding from './expanding/SourceDataFormLayoutExpanding';
import SourceDataFormLayoutTitled from './titled/SourceDataFormLayoutTitled';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import useSourceDataEventReviewCount from '@/hooks/form/useSourceDataEventReviewCount';
import useSourceDataReviewCounts from '@/hooks/form/useSourceDataReviewCounts';
import SourceDataReviewCountsContext from '@/contexts/forms/SourceDataReviewCountsContext';
import { useSWRConfig } from 'swr';
import { DateTime } from 'luxon';
import { MonitoringVisitStatus, PostSaveSourceData } from '../../../api/dtos';
import { processReviewCounts, ISourceDataReviewCounts } from '../../../hooks/form/useSourceDataReviewCounts';
import SourceDataFormConfirmation from './SourceDataFormConfirmation';

const DEFAULT_SETTINGS: ISourceDataFormSettings = {
    layoutType: 'Titled',
    hideEvents: true,
    hideForms: true,
    hideVersions: false,
    autoCollections: false,
    autoEvents: false,
    autoForms: false,
    autoGroups: false,
    showAudit: true,
    showNotes: true,
    showSideNavigation: true,
    showSideNavigationForms: true,
    showSideNavigationReviewProgress: true,
    showSideNavigationVerificationProgress: true
};
const Title = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center'
}));

const DateChip = styled(Chip)(({theme}) => ({
    // '& svg': {
    //     transform: 'scale(0.75)'
    // },
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2)
}));

interface ISourceDataFormProps {
    readOnly?: boolean;
    updateSourceData?: (newData?: ISourceData) => Promise<void>;
    hasBreadcrumbs?: boolean;
    patient?: IPatient;
    institution?: IInstitution;
    trial?: ITrial;
    data?: ISourceData;
    configuration?: ISourceDataConfiguration;
    trialConfiguration?: ISourceDataConfiguration;
    metadata?: ITrialFormMetadata;
    loading?: boolean;
    updating?: boolean;
    hideVerification?: boolean;
}

const SourceDataForm: FunctionComponent<ISourceDataFormProps> = ({
    readOnly,
    configuration,
    trialConfiguration,
    data,
    metadata,
    trial,
    patient,
    institution,
    updateSourceData,
    hideVerification,
    loading,
    updating,
    hasBreadcrumbs
}) => {
    const formsFeature = useContext(FormsFeatureContext);

    const navigate = useNavigate();
    const { trialCode } = useParams();

    const [isUpdating, setIsUpdating] = useState(false);

    const [settings, setSettings] = useLocalStorage<ISourceDataFormSettings>('source-data-form-settings', DEFAULT_SETTINGS)

    const updateSettings = useCallback((update :((current: ISourceDataFormSettings) => ISourceDataFormSettings)) => {
        setSettings(current => update(current ?? DEFAULT_SETTINGS))
    }, [setSettings])

    const formProcessor = useMemo(() => {
        return new SourceDataFormProcessor();
    }, []);

    
    const [processedEvents, setProcessedEvents] = useState<ISourceDataVerificationEvent[]>([])

    useEffect(() => {
        const processEvents = async () => {
            const events = await formProcessor.processAllEvents(configuration, trialConfiguration, data, metadata);
            setProcessedEvents(events);
        }

        processEvents();
    }, [formProcessor, configuration, trialConfiguration, data, metadata, setProcessedEvents]);

    const processedList = useMemo(() => {
        const list: ISourceDataVerificationItem[] = [];

        for (let event of processedEvents) {
            if (!event.eventEnabled) {
                continue;
            }

            if (!event.eventSourceData && settings?.hideEvents) {
                continue;
            }

            list.push(event);

            if (!event.eventSourceData) {
                continue;
            }

            if (event.forms) {
                for (let form of event.forms) {
                    if (!form.formEnabled) {
                        continue;
                    }
        
                    if (!form.formSourceData && settings?.hideForms) {
                        continue;
                    }
        
                    if (!form.formVersionEnabled && settings?.hideVersions) {
                        continue;
                    }

                    list.push(form)

                    if (!form.formSourceData) {
                        continue;
                    } 
                    
                    if (!form.formVersionEnabled) {
                        continue;
                    }

                    if (form.fields) {
                        for (let field of form.fields) {
                            if (field.type === SourceDataType.Field && !field.formFieldEnabled) {
                                continue;
                            }

                            if (field.type === SourceDataType.Section && !field.formSectionEnabled) {
                                continue;
                            }

                            list.push(field)

                            if (field.type === SourceDataType.Section && field.fields) {
                                
                                for (let sectionField of field.fields) {
                                    if (!sectionField.formFieldEnabled) {
                                        continue;
                                    }

                                    list.push(sectionField)
                                }
                            }
                        }
                    }
                }
            }
        }

        return list;
    }, [processedEvents, settings]);

    const [reviewCounts, setReviewCounts] = useState<ISourceDataReviewCounts>();
    
    const onSubmit: IFormSubmit<ISourceData, IValidationError> = useCallback(async ( state ) => {
        if (!state.value) {
            return {}
        }   

        if (!state.value.id || !state.value.monitoringVisitId) {
            return {};
        }

        if (!reviewCounts) {
            const counts = processReviewCounts(processedEvents, state.value.events);
    
            if (counts.review.reviewed !== counts.review.totalToReview ||
                counts.review.verified !== counts.review.totalToVerify) {
                setReviewCounts(counts);
                
                return {};
            }
        }
        
        const response = await formsFeature.serviceStackClient.post(new PostUpdateSourceDataStatus({ sourceDataId: state.value.id, monitoringVisitId: state.value.monitoringVisitId, status: reviewCounts ?  MonitoringVisitStatus.Incomplete : MonitoringVisitStatus.Complete }))

        navigate(`/${trialCode}/monitoring-visits/${state.value.monitoringVisitId}`);

        return {}
    }, [formsFeature.serviceStackClient, navigate, trialCode, processedEvents, reviewCounts, setReviewCounts]);

    const Layout: ISourceDataFormLayoutComponents = useMemo(() => {
        if (settings?.layoutType === 'Expanding') {
            return {
                Layout: SourceDataFormLayoutExpanding,
                Event: SourceDataFormEventExpanding,
                Form: SourceDataFormFormExpanding,
                Collection: SourceDataFormCollectionExpanding,
                Group: SourceDataFormGroupExpanding,
                Field: SourceDataFormFieldExpanding
            }
        }

        return {
            Layout: SourceDataFormLayoutTitled,
            Event: SourceDataFormEventTitled,
            Form: SourceDataFormFormTitled,
            Collection: SourceDataFormCollectionTitled,
            Group: SourceDataFormGroupTitled,
            Field: SourceDataFormFieldTitled
        }
    }, [settings?.layoutType]);

    const content = useMemo(() => {
        return (
            <Layout.Layout
                hasBreadcrumbs={hasBreadcrumbs}
                readOnly={readOnly || isUpdating}
                sourceData={data}
                events={processedEvents}
                settings={settings}
                list={processedList}
                Layout={Layout}
                trial={trial}
                institution={institution}
                patient={patient}
                hideVerification={hideVerification}
            />
        )
    }, [processedEvents, settings, Layout, processedList, trial, patient, institution, readOnly, isUpdating, hideVerification])

    return (
        <Form
            onSubmit={onSubmit}
            initialValue={data}
            debug={import.meta.env.DEV}
            globalErrorPropertyPath="submit"
        >
            <SourceDataFormConfirmation counts={reviewCounts} setCounts={setReviewCounts} />
            {/* <SourceDataReviewCountsProvider events={processedEvents}> */}
                <Title>
                    <Typography variant="h1" gutterBottom>
                        {configuration?.name}
                    </Typography>
                    
                    <Box
                        sx={{ml: 'auto', display: 'flex'}}
                    >
                        { 
                            data?.enteredDate && (
                                <Tooltip title="Snapshot Date">
                                    <DateChip  icon={<FontAwesomeIcon icon={faCalendarDays} />} label={DateTime.fromISO(data?.updatedDate ? data.updatedDate : data.enteredDate).toFormat('dd/MM/yyyy - hh:mm:ss a')} />
                                </Tooltip>
                            )
                        }
                        {
                            !data?.id && (
                                <Tooltip title="Refresh Data">
                                    <IconButton 
                                        sx={{ ml: 2, mr: 2 }}
                                        onClick={async() => {
                                            setIsUpdating(true);

                                            try {
                                                if (updateSourceData) {
                                                    await updateSourceData(data);
                                                }
                                            }
                                            finally 
                                            {
                                                setIsUpdating(false);
                                            }
                                        }}
                                        disabled={isUpdating}
                                    >
                                        <FontAwesomeIcon icon={faRefresh} className={isUpdating ? 'fa-spin' : undefined} />
                                    </IconButton>
                                </Tooltip>
                            )
                        }
                        {
                            !readOnly && data?.id && (
                                <Tooltip title="Update Data">
                                    <IconButton 
                                        sx={{ ml: 2, mr: 2 }}
                                        onClick={async() => {
                                            setIsUpdating(true);

                                            try {
                                                const response = await formsFeature.serviceStackClient.post(new PostUpdateMonitoringVisitData({
                                                    sourceDataId: data.id,
                                                    sourceDataConfigurationId: data.sourceDataConfigurationId
                                                }))

                                                if (updateSourceData) {
                                                    await updateSourceData(response.data);
                                                }
                                            }
                                            finally 
                                            {
                                                setIsUpdating(false);
                                            }
                                        }}
                                        disabled={isUpdating}
                                    >
                                        <FontAwesomeIcon icon={faRefresh} className={isUpdating ? 'fa-spin' : undefined} />
                                    </IconButton>
                                </Tooltip>
                            )
                        }
                        <SourceDataFormSettings settings={settings ?? DEFAULT_SETTINGS} setSettings={updateSettings} />
                    </Box>
                </Title>
                {content}
            {/* </SourceDataReviewCountsProvider> */}
        </Form>
    );
}

interface ISourceDataReviewCountsProviderProps extends IHasChildren {
    events: ISourceDataVerificationEvent[]
}

const SourceDataReviewCountsProvider: FC<ISourceDataReviewCountsProviderProps> = ({
    events,
    children
}) => {
    

    const counts = useSourceDataReviewCounts(events)

    const deferredCounts = useDeferredValue(counts);

    return (
        <SourceDataReviewCountsContext.Provider value={deferredCounts}>
            {children}
        </SourceDataReviewCountsContext.Provider>
    )
}

export default SourceDataForm;